import { useCallback, useEffect, useRef, useState } from "react";
import { T, fieldInput, fmtDate } from "./theme";
import { Btn } from "./components";
import LicenseGoalChart from "./LicenseGoalChart";
import {
  fetchLicenseGoal,
  fetchLicenseGoalProgress,
  fetchProvisionedCountAsOf,
  saveLicenseGoal,
} from "./api";
import {
  computeGoalStats,
  formatLongDate,
  formatShortDate,
  quarterBounds,
  quarterOf,
  todayIso,
} from "./licenseGoalMath";

const QUARTERS = [1, 2, 3, 4];

const yearOptions = (selectedYear) => {
  const thisYear = new Date().getFullYear();
  const years = new Set([thisYear - 1, thisYear, thisYear + 1, selectedYear]);
  return [...years].sort((a, b) => a - b);
};

const selectStyle = { ...fieldInput, width: "auto", fontSize: 12, padding: "5px 10px" };
const labelStyle = { fontSize: 12, color: T.textMuted, display: "flex", flexDirection: "column", gap: 4 };

const emptyForm = (year, quarter) => ({
  year,
  quarter,
  ...quarterBounds(year, quarter),
  goal_count: "",
  start_count: "",
});

function StatTile({ label, value, caption }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderTop: `3px solid ${T.accent}`, borderRadius: T.radius, padding: "14px 16px" }}>
      <div style={{ fontSize: 12, color: T.textDim }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: T.text, margin: "6px 0 4px" }}>{value}</div>
      <div style={{ fontSize: 11, color: T.textDim }}>{caption}</div>
    </div>
  );
}

function GoalForm({ initial, saving, error, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const [startCountTouched, setStartCountTouched] = useState(initial.start_count !== "");
  const [prefilling, setPrefilling] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const requestId = useRef(0);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const loadQuarter = async (year, quarter) => {
    const id = ++requestId.current;
    setPrefilling(true);
    setLookupError("");
    try {
      const res = await fetchLicenseGoal({ year, quarter });
      if (id !== requestId.current) return;
      if (res.error) {
        setLookupError(res.error);
        return;
      }
      if (res.data) {
        setForm({
          year,
          quarter,
          start_date: res.data.start_date,
          end_date: res.data.end_date,
          goal_count: res.data.goal_count,
          start_count: res.data.start_count,
        });
        setStartCountTouched(true);
      } else {
        setForm(emptyForm(year, quarter));
        setStartCountTouched(false);
        await prefillStartCount(quarterBounds(year, quarter).start_date, id);
      }
    } catch (err) {
      if (id === requestId.current) setLookupError(err.message || "Failed to load the quarter's goal");
    } finally {
      if (id === requestId.current) setPrefilling(false);
    }
  };

  const prefillStartCount = async (date, id = ++requestId.current) => {
    setLookupError("");
    try {
      const res = await fetchProvisionedCountAsOf(date);
      if (id !== requestId.current) return;
      if (res.error) setLookupError(res.error);
      else if (res.data) update({ start_count: res.data.count });
    } catch (err) {
      if (id === requestId.current) setLookupError(err.message || "Failed to look up the starting count");
    }
  };

  const onStartCountChange = (value) => {
    requestId.current += 1;
    setPrefilling(false);
    setLookupError("");
    setStartCountTouched(true);
    update({ start_count: value });
  };

  useEffect(() => {
    if (initial.start_count === "" && initial.start_date) prefillStartCount(initial.start_date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStartDateChange = (value) => {
    update({ start_date: value });
    if (!startCountTouched && value) prefillStartCount(value);
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      year: Number(form.year),
      quarter: Number(form.quarter),
      goal_count: form.goal_count === "" ? null : Number(form.goal_count),
      start_count: form.start_count === "" ? null : Number(form.start_count),
      start_date: form.start_date,
      end_date: form.end_date,
    });
  };

  return (
    <form
      onSubmit={submit}
      style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, marginBottom: 20 }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
        <label style={labelStyle}>
          Year
          <select style={fieldInput} value={form.year} onChange={(e) => loadQuarter(Number(e.target.value), form.quarter)}>
            {yearOptions(Number(form.year)).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          Quarter
          <select style={fieldInput} value={form.quarter} onChange={(e) => loadQuarter(form.year, Number(e.target.value))}>
            {QUARTERS.map((q) => <option key={q} value={q}>Q{q}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          Goal (licenses)
          <input style={fieldInput} type="number" min="0" step="1" value={form.goal_count} onChange={(e) => update({ goal_count: e.target.value })} required />
        </label>
        <label style={labelStyle}>
          Start date
          <input style={fieldInput} type="date" value={form.start_date} onChange={(e) => onStartDateChange(e.target.value)} required />
        </label>
        <label style={labelStyle}>
          End date
          <input style={fieldInput} type="date" value={form.end_date} onChange={(e) => update({ end_date: e.target.value })} required />
        </label>
        <label style={labelStyle}>
          Starting licenses
          <input
            style={fieldInput}
            type="number"
            min="0"
            step="1"
            value={form.start_count}
            onChange={(e) => onStartCountChange(e.target.value)}
            required
          />
        </label>
      </div>
      {lookupError && <div style={{ color: T.red, fontSize: 13, marginTop: 12 }}>{lookupError}</div>}
      {error && <div style={{ color: T.red, fontSize: 13, marginTop: 12 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Btn variant="primary" type="submit" disabled={saving || prefilling}>{saving ? "Saving..." : "Save goal"}</Btn>
        <Btn type="button" onClick={onCancel}>Cancel</Btn>
      </div>
    </form>
  );
}

export default function LicenseGoal({ showToast }) {
  const [selected, setSelected] = useState(() => quarterOf());
  const [goal, setGoal] = useState(null);
  const [progress, setProgress] = useState({ points: [], current_count: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const loadId = useRef(0);

  const load = useCallback(async ({ year, quarter }) => {
    const id = ++loadId.current;
    setLoading(true);
    setLoadError("");
    try {
      const [goalRes, progressRes] = await Promise.all([
        fetchLicenseGoal({ year, quarter }),
        fetchLicenseGoalProgress({ year, quarter }),
      ]);
      if (id !== loadId.current) return;
      if (goalRes.error) throw new Error(goalRes.error);
      if (progressRes.error) throw new Error(progressRes.error);
      setGoal(goalRes.data);
      setProgress(progressRes.data);
    } catch (err) {
      if (id !== loadId.current) return;
      setGoal(null);
      setLoadError(err.message || "Failed to load license goal");
    } finally {
      if (id === loadId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(selected);
  }, [load, selected]);

  const handleSave = async (payload) => {
    setSaving(true);
    setSaveError("");
    try {
      const res = await saveLicenseGoal(payload);
      if (res.error) {
        setSaveError(res.error);
        return;
      }
      setEditing(false);
      showToast("License goal saved");
      const next = { year: payload.year, quarter: payload.quarter };
      if (next.year === selected.year && next.quarter === selected.quarter) load(next);
      else setSelected(next);
    } catch (err) {
      setSaveError(err.message || "Failed to save license goal");
    } finally {
      setSaving(false);
    }
  };

  const openEditor = () => {
    setSaveError("");
    setEditing(true);
  };

  const formInitial = goal
    ? {
        year: goal.year,
        quarter: goal.quarter,
        start_date: goal.start_date,
        end_date: goal.end_date,
        goal_count: goal.goal_count,
        start_count: goal.start_count,
      }
    : emptyForm(selected.year, selected.quarter);

  const today = progress.as_of || todayIso();
  const currentCount = progress.current_count ?? goal?.start_count ?? 0;
  const stats = goal
    ? computeGoalStats({
        goalCount: goal.goal_count,
        startCount: goal.start_count,
        startDate: goal.start_date,
        endDate: goal.end_date,
        currentCount,
        today,
      })
    : null;

  return (
    <div style={{ fontFamily: T.font, color: T.text }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "linear-gradient(135deg, #5b21b6, #4c1d95)", borderRadius: T.radiusSm, padding: "12px 20px", marginBottom: 28 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <select
            aria-label="Year"
            style={selectStyle}
            value={selected.year}
            onChange={(e) => setSelected((s) => ({ ...s, year: Number(e.target.value) }))}
          >
            {yearOptions(selected.year).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            aria-label="Quarter"
            style={selectStyle}
            value={selected.quarter}
            onChange={(e) => setSelected((s) => ({ ...s, quarter: Number(e.target.value) }))}
          >
            {QUARTERS.map((q) => <option key={q} value={q}>Q{q}</option>)}
          </select>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: "#ede9fe", textTransform: "uppercase" }}>
          Q{selected.quarter} {selected.year} · License Goal
        </span>
      </div>

      {editing && (
        <GoalForm
          key={`${selected.year}-${selected.quarter}-${goal ? "edit" : "new"}`}
          initial={formInitial}
          saving={saving}
          error={saveError}
          onCancel={() => setEditing(false)}
          onSave={handleSave}
        />
      )}

      {loading && <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Loading license goal...</div>}

      {!loading && loadError && (
        <div style={{ padding: 24, color: T.red, background: T.redSoft, borderRadius: T.radius }}>{loadError}</div>
      )}

      {!loading && !loadError && !goal && !editing && (
        <div style={{ textAlign: "center", padding: "56px 20px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No goal set for Q{selected.quarter} {selected.year}</div>
          <div style={{ color: T.textMuted, fontSize: 14, marginBottom: 20 }}>Set a license goal to track progress against the pace needed to reach it.</div>
          <Btn variant="primary" onClick={openEditor}>Set goal</Btn>
        </div>
      )}

      {!loading && !loadError && goal && stats && (
        <>
          <h2 style={{ fontSize: 24, fontWeight: 500, margin: "0 0 8px" }}>Road to {goal.goal_count.toLocaleString()} licenses</h2>
          <p style={{ color: T.textMuted, fontSize: 14, margin: "0 0 24px", maxWidth: 560, lineHeight: 1.5 }}>
            {stats.goalReached
              ? `Goal reached. We've added ${stats.added.toLocaleString()} licenses since ${formatShortDate(goal.start_date)}.`
              : stats.daysLeft === 0
                ? `The goal period ended ${formatShortDate(goal.end_date)} with ${stats.remaining.toLocaleString()} licenses still to go.`
                : `Since ${formatShortDate(goal.start_date)} we've added ${stats.added.toLocaleString()} licenses. To hit our goal of ${goal.goal_count.toLocaleString()} by ${formatShortDate(goal.end_date)} we need about ${stats.neededPerWeek.toLocaleString()} new licenses a week.`}
          </p>

          <div style={{ background: "linear-gradient(135deg, #6d28d9, #4c1d95)", borderRadius: T.radius, padding: "28px 28px 20px", color: "#fff", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
              <span style={{ fontSize: 80, fontWeight: 500, lineHeight: 1 }}>
                {stats.goalReached ? "Goal reached" : stats.remaining.toLocaleString()}
              </span>
              {!stats.goalReached && <span style={{ fontSize: 14, opacity: 0.8 }}>licenses to go</span>}
            </div>
            <div style={{ height: 12, borderRadius: 6, background: "rgba(255,255,255,0.18)", margin: "24px 0 8px", overflow: "hidden" }}>
              <div style={{ width: `${stats.pctOfWay}%`, height: "100%", borderRadius: 6, background: "rgba(255,255,255,0.9)" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.85 }}>
              <span>Start {goal.start_count.toLocaleString()}</span>
              <span style={{ fontWeight: 600 }}>{stats.pctOfWay.toFixed(1)}% of the way</span>
              <span>Goal {goal.goal_count.toLocaleString()}</span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 20 }}>
            <StatTile label="Current licenses" value={currentCount.toLocaleString()} caption={`As of ${formatLongDate(today)}`} />
            <StatTile label="Starting point" value={goal.start_count.toLocaleString()} caption={formatLongDate(goal.start_date)} />
            <StatTile label="Added so far" value={stats.added.toLocaleString()} caption={`Since ${formatShortDate(goal.start_date)}`} />
            <StatTile label="Days left" value={stats.daysLeft.toLocaleString()} caption={`From ${formatShortDate(today)} to ${formatShortDate(goal.end_date)}`} />
            <StatTile label="Needed per week" value={stats.neededPerWeek.toLocaleString()} caption="To finish on time" />
          </div>

          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "20px 20px 12px", marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Licenses vs. the pace to our goal</div>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 12 }}>
              Solid line is our actual count at each weekly update. Dashed line is the steady pace from {goal.start_count.toLocaleString()} on {formatShortDate(goal.start_date)} to the goal ({formatShortDate(goal.start_date)} + {stats.totalSpan.toLocaleString()}) on {formatShortDate(goal.end_date)}.
            </div>
            <LicenseGoalChart goal={goal} points={progress.points} />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: T.textDim }}>
            <span>Last updated by {goal.updated_by} on {fmtDate(goal.updated_at)}</span>
            {!editing && <Btn sm onClick={openEditor}>Edit goal</Btn>}
          </div>
        </>
      )}
    </div>
  );
}
